import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2, Loader2, MapPin, ArrowLeft, User, Phone, Mail, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_INTERESTS = [
  "Gold IRA",
  "Silver IRA", 
  "Precious Metals (Physical)",
  "Numismatic Coins",
  "Bullion",
  "Estate Planning",
  "Other"
];

const INVESTMENT_TIMELINES = [
  "Immediate (< 1 month)",
  "Short-term (1-3 months)",
  "Medium-term (3-6 months)",
  "Long-term (6+ months)",
  "Just exploring"
];

const LANGUAGE_PREFERENCES = [
  "English",
  "Spanish",
  "Other"
];

export default function PublicFieldDataPage() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    product_interest: '',
    investment_timeline: '',
    language_preference: '',
    notes: '',
    consent: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.phone && !formData.email) {
      setError('Please provide either a phone number or email address');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.consent) {
      setError('Please accept the consent to proceed');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/public/field-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setSubmitted(true);
        toast.success(data.message);
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
        toast.error(data.detail || 'Failed to submit');
      }
    } catch (err) {
      logger.error('Submit error:', err);
      setError('Network error. Please check your connection and try again.');
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      product_interest: '',
      investment_timeline: '',
      language_preference: '',
      notes: '',
      consent: false
    });
    setSubmitted(false);
    setError('');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12">
        <div className="max-w-md mx-auto px-4" data-testid="field-data-success">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
              <p className="text-slate-600 mb-6">
                Your information has been submitted successfully. One of our representatives will contact you soon.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={resetForm}
                  className="w-full bg-gold hover:bg-gold/90 text-slate-900"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Submit Another Entry
                </Button>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8">
      <div className="max-w-md mx-auto px-4" data-testid="public-field-data-page">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <img 
              src="/logo-blue.png" 
              alt="Goldsands" 
              className="h-16 w-16 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Quick Lead Entry</h1>
          <p className="text-sm text-slate-500 mt-1">
            Capture prospect information on the go
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <MapPin className="h-5 w-5" />
              Field Data Entry Form
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="First name"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      className="pl-9"
                      data-testid="first-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Last Name</Label>
                  <Input
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="space-y-1.5">
                <Label className="text-sm">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="pl-9"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="pl-9"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-400 -mt-2">
                * Phone or email required
              </p>

              {/* Interest Fields */}
              <div className="space-y-1.5">
                <Label className="text-sm">Product Interest</Label>
                <Select 
                  value={formData.product_interest} 
                  onValueChange={(v) => handleChange('product_interest', v)}
                >
                  <SelectTrigger data-testid="product-interest-select">
                    <SelectValue placeholder="Select product interest" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_INTERESTS.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Investment Timeline</Label>
                <Select 
                  value={formData.investment_timeline} 
                  onValueChange={(v) => handleChange('investment_timeline', v)}
                >
                  <SelectTrigger data-testid="timeline-select">
                    <SelectValue placeholder="When are they looking to invest?" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVESTMENT_TIMELINES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Language Preference</Label>
                <Select 
                  value={formData.language_preference} 
                  onValueChange={(v) => handleChange('language_preference', v)}
                >
                  <SelectTrigger data-testid="language-select">
                    <SelectValue placeholder="Preferred language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_PREFERENCES.map(l => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-sm">Notes</Label>
                <Textarea
                  placeholder="Any additional information about the prospect..."
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Consent */}
              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => handleChange('consent', checked)}
                  data-testid="consent-checkbox"
                />
                <Label htmlFor="consent" className="text-xs text-slate-500 leading-tight cursor-pointer">
                  I confirm this person has given verbal consent to be contacted by Goldsands Group Inc regarding investment opportunities.
                </Label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gold hover:bg-gold/90 text-slate-900 font-semibold"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4 mr-2" />
                    Submit Lead
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="text-center mt-6 space-y-3">
          <Link 
            to="/login" 
            className="text-sm text-slate-500 hover:text-gold transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
